import {
  default as Icon,
  default as MaterialCommunityIcons,
} from '@expo/vector-icons/MaterialCommunityIcons';
import { DateTypeFilter, Workspace } from '@gripp/shared-logic';
import { Colors, DropDownView, LoadingIndicator } from '@gripp/shared-ui';
import { useRef, useState } from 'react';
import { CSVLink } from 'react-csv';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { Text } from 'react-native-paper';
import { DataExportModal } from './dataExportModal';
import { useExportHook } from './types';

type ExportButtonProps = {
  icon: React.ComponentProps<typeof MaterialCommunityIcons>['name'];
  useExport: useExportHook;
  fileName: string;
  workspace: Workspace;
  label?: string;
};

export const ExportButton = (props: ExportButtonProps) => {
  const csvRef = useRef<any>(null);
  const buttonRef = useRef<View>(null);
  const [download, toggleDownload] = useState(false);
  const [showDataExportModal, setShowDataExportModal] = useState(false);
  const [dateFilter, setDateFilter] = useState<{
    dateType: DateTypeFilter;
    startDate?: string;
    endDate?: string;
  }>({
    dateType: DateTypeFilter.ALL,
  });

  const onDataReady = () => {
    if (download) doDownload();
  };

  const { headers, rows, isLoading } = props.useExport({
    download: download,
    workspace: props.workspace,
    onDataReady: onDataReady,
    dateFilter: dateFilter,
  });

  const doDownload = () => {
    setTimeout(() => {
      csvRef.current?.link?.click();
      toggleDownload(false);
      // Reset the date filter after download to remove the filter icon
      setDateFilter({
        dateType: DateTypeFilter.ALL,
      });
    }, 50);
  };

  const onDownload = () => {
    // Always show data export modal first
    setShowDataExportModal(true);
  };

  const onDataExportDownload = (filter: {
    dateType: DateTypeFilter;
    startDate?: string;
    endDate?: string;
  }) => {
    setDateFilter(filter);
    // Trigger the download with the selected date filter
    toggleDownload(true);
  };

  return (
    <>
      <View ref={buttonRef}>
        <TouchableOpacity
          onPress={onDownload}
          disabled={isLoading}
          style={styles.button}
          activeOpacity={0.85}
        >
          {isLoading ? (
            <LoadingIndicator backgroundColor={Colors.black} size={12} />
          ) : (
            <>
              <Icon name={props.icon} style={styles.icon} size={14} />
              <Text style={styles.name}>{props?.label ?? props.fileName}</Text>
            </>
          )}
        </TouchableOpacity>
      </View>
      <CSVLink
        data={rows}
        headers={headers}
        filename={
          props.workspace
            ? `${props.workspace.name}-${props.fileName}.csv`
            : `${props.fileName}.csv`
        }
        ref={csvRef}
      />
      <DropDownView
        isVisible={showDataExportModal}
        onDismiss={() => setShowDataExportModal(false)}
        anchorRef={buttonRef as any}
        width={450}
        height={650}
        maxHeight={650}
      >
        <DataExportModal
          visible={showDataExportModal}
          onDismiss={() => setShowDataExportModal(false)}
          onDownload={onDataExportDownload}
          initialFilter={dateFilter}
        />
      </DropDownView>
    </>
  );
};

const styles = StyleSheet.create({
  button: {
    backgroundColor: Colors.black,
    minWidth: 93,
    maxWidth: 100,
    height: 28,
    paddingTop: 6,
    paddingRight: 10,
    paddingBottom: 6,
    paddingLeft: 10,
    borderRadius: 8,
    display: 'flex',
    justifyContent: 'center',
    alignContent: 'center',
    flexDirection: 'row',
    alignItems: 'center',
  },
  name: {
    color: Colors.white,
    textTransform: 'capitalize',
    fontSize: 14,
  },
  icon: {
    marginRight: 6,
    marginTop: 1,
    color: Colors.white,
  },
});
